import {
  Card,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Link,
  Stack,
  styled,
  SxProps,
  Typography,
} from "@mui/material";
import * as React from "react";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useMultipleSelection } from "../../../Utils/Hooks/useMultipleSelection";
import TitleItem from "../../Lists/TitleItemList/TitleItem";
import { useTranslation } from "react-i18next";

export interface RequestCardProps {
  sx?: SxProps;
  id: string;
  cover: string;
  title: string;
  contents: { name: string; id: number; childCount: number }[];
  onContentChange?: (selectedIds: number[]) => void;
  onAddRequestClick?: (data: { id: string; selectedContents: number[] }) => void;
  onRemoveRequestClick?: (id: string) => void;
  alreadyAddedToRequest?: boolean;
  atLeastOneContentSelected?: boolean;
}

const RequestCard: React.FC<RequestCardProps> = ({
  id,
  cover,
  title,
  contents,
  sx = {},
  onContentChange,
  onAddRequestClick,
  onRemoveRequestClick,
  alreadyAddedToRequest,
  atLeastOneContentSelected = true,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const { isSelected, select, selected } = useMultipleSelection<number>();

  const handleCardHeaderClick: React.MouseEventHandler = () => {
    setExpanded((prevState) => !prevState);
  };

  const handleCheckboxClick = (id: number): React.MouseEventHandler => {
    return () => {
      select(id);
    };
  };

  const handleLinkClick: React.MouseEventHandler = (e) => {
    e.stopPropagation();

    if (!onAddRequestClick) {
      return;
    }

    onAddRequestClick({ id, selectedContents: selected });
  };

  const handleDeleteIconClick: React.MouseEventHandler = (e) => {
    e.stopPropagation();

    if (!onRemoveRequestClick) {
      return;
    }

    onRemoveRequestClick(id);
  };

  React.useEffect(() => {
    onContentChange && onContentChange(selected);
  }, [selected]);

  return (
    <StyledCard expanded={expanded} sx={sx} variant="outlined">
      <Stack
        sx={{ cursor: "pointer", p: 1, pr: 1 }}
        onClick={handleCardHeaderClick}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <TitleItem size="small" poster={cover} posterProps={{ height: 44, width: 32 }} title={title}>
          {" "}
          {contents.length > 0 && (
            <Stack direction="row" alignItems="center">
              <Typography color={atLeastOneContentSelected && !selected.length ? "error.main" : "text.tier"}>
                {t("component:body.portionContentsSelected", {
                  selectedLength: selected.length,
                  totalLength: contents.length,
                })}
              </Typography>
              <ExpandMoreRoundedIcon color={atLeastOneContentSelected && !selected.length ? "error" : "disabled"} />
            </Stack>
          )}
        </TitleItem>
        {alreadyAddedToRequest ? (
          <IconButton color="error" onClick={handleDeleteIconClick}>
            <DeleteOutlinedIcon />
          </IconButton>
        ) : (
          <Link sx={{ mr: 1.5 }} color="text.primary" underline="hover" variant="button" onClick={handleLinkClick}>
            <AddIcon />
            {t("button:addToRequest")}
          </Link>
        )}
      </Stack>
      {contents && contents.length > 0 && (
        <Collapse sx={{ pl: 1, pr: 1 }} in={expanded}>
          <FormGroup sx={{ ml: 5, mb: 1 }}>
            {contents
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((content, index) => (
                <React.Fragment key={`checkbox-${index}`}>
                  {index !== 0 && <Divider />}
                  <FormControlLabel
                    sx={{ ml: -1, ".MuiFormControlLabel-label": { fontWeight: `500 !important` } }}
                    control={<Checkbox checked={isSelected(content.id)} onClick={handleCheckboxClick(content.id)} />}
                    label={`${content.name} | ${content.childCount} items`}
                  />
                </React.Fragment>
              ))}
          </FormGroup>
        </Collapse>
      )}
    </StyledCard>
  );
};

export default RequestCard;

const StyledCard = styled(Card, { shouldForwardProp: (propName) => propName !== "expanded" })<{ expanded: boolean }>(
  ({ theme, expanded }) => ({
    "&:hover": {
      background: theme.palette.backgroundAlpha.hover,
    },
    ...(expanded && {
      background: theme.palette.backgroundAlpha.pressed,
    }),
  })
);
